// Copy for /our-work/data-engineering (Data Warehousing).
// Stairway step names come from ./stairway.ts (shared with Data Maturity and /how-it-works).
// Platform names, logo labels and product names (Azure, BigQuery, dbt, Looker…) stay in the page.

type PlatformCopy = { description: string; services: string[] };

const en = {
  seoTitle: 'Data Warehousing | Stratodata',
  seoDescription:
    'We design, build and run data infrastructure across Microsoft Azure, MS Fabric, Google Cloud Platform and AWS — turning raw sources into a single verified source of truth.',
  hero: {
    title: 'Data Warehousing',
    subtitle: 'The foundation every data-driven organisation is built on.',
    text: 'We design, build and run the data infrastructure that turns raw sources into a single verified truth — across Microsoft Azure, MS Fabric, Google Cloud Platform and AWS.',
    cta: 'Let\'s build a reliable data foundation',
    // Small line inside the primary button; empty = not shown
    ctaNote: '',
    stairwayLink: 'See the full stairway →',
  },
  step3: {
    eyebrow: 'Step 3 of the Stratodata Stairway',
    title: 'Single Source of Truth: DWH & BI',
    // **…** = highlighted phrase
    text: 'Data engineering is the critical **leap from chaos to clarity** across your company. Without a verified, unified data model, automation, AI and data-driven culture are all **built on sand**. We build the foundation so everything above it works.',
  },
  platformsSection: {
    title: 'Cross-platform expertise',
    subtitle: 'Multiple production deployments delivered across all major cloud platforms. We go where your business already lives.',
    discuss: (name: string) => `Discuss a ${name} project →`,
    servicesLabel: 'Services we deliver',
  },
  platforms: {
    azure: {
      description: 'End-to-end data pipelines on the Microsoft ecosystem. Seamless integration with existing Office 365 and Dynamics environments.',
      services: ['Azure Data Factory', 'Azure Synapse Analytics', 'Azure Data Lake Storage', 'Azure Databricks', 'Azure SQL Database', 'Power BI'],
    },
    fabric: {
      description: 'Microsoft\'s next-generation unified analytics platform. We are early adopters — delivering production Fabric deployments since its GA release.',
      services: ['Lakehouse architecture', 'OneLake unified storage', 'Data Factory pipelines', 'Real-time analytics', 'Power BI Direct Lake', 'Fabric notebooks'],
    },
    gcp: {
      description: 'Our deepest expertise. BigQuery-first architectures that scale from startup to enterprise, powering RAS and advanced AI analytics.',
      services: ['BigQuery', 'Cloud Composer (Airflow)', 'Dataform', 'Looker', 'Cloud Storage', 'Vertex AI'],
    },
    aws: {
      description: 'Robust data infrastructure on the world\'s largest cloud. Purpose-built pipelines for retailers migrating from on-premise to cloud-native.',
      services: ['Amazon Redshift', 'AWS Glue', 'S3 Data Lakes', 'Lambda ETL', 'Amazon RDS', 'SageMaker'],
    },
  } as Record<'azure' | 'fabric' | 'gcp' | 'aws', PlatformCopy>,
  capabilitiesSection: {
    title: 'What we build',
    subtitle: 'End-to-end data engineering — from raw sources to actionable truth.',
  },
  /** Same order as the icons in the page. */
  capabilities: [
    {
      title: 'Data Warehouse Design',
      description: 'Dimensional modelling, star schemas, and lakehouse architectures purpose-built for retail. We design for query performance, cost efficiency, and the AI workloads of tomorrow.',
    },
    {
      title: 'ETL / ELT Pipelines',
      description: 'Automated, monitored, and alerting pipelines that move data from every source — ERP, e-commerce, marketing, WMS — into a single verified model. Daily freshness, every time.',
    },
    {
      title: 'BI & Reporting Layer',
      description: 'Dashboards that your whole organisation actually uses. From executive North Star views to operational daily reports — built in Looker, Power BI, or embedded directly in your tools.',
    },
    {
      title: 'Data Quality & Governance',
      description: 'We don\'t just move data — we verify it. Automated tests, reconciliation checks, and anomaly alerts that catch problems before they reach dashboards. Your team can trust the numbers.',
    },
    {
      title: 'AI-Ready Data Models',
      description: 'We structure data the way AI models need to consume it — context-rich, relationship-mapped, and semantically clear. Your data warehouse becomes the foundation for reliable AI analytics.',
    },
    {
      title: 'Migrations & Modernisation',
      description: 'Moving from legacy on-premise to cloud-native, or from one cloud to another. We\'ve run cross-platform migrations across all major stacks — with zero business disruption.',
    },
  ],
  ai: {
    eyebrow: 'Why it matters for AI',
    titleLine1: 'Bad data in.',
    titleLine2: 'Bad AI out.',
    p1: 'AI models are only as reliable as the data they\'re built on. A fragmented, inconsistent data landscape produces hallucinations, contradictions, and decisions you can\'t trust.',
    p2: 'Our data engineering work creates the structured, verified, semantically rich foundation that makes AI analytics work — not sometimes, but every time.',
    p3: 'From our unified retail data model to the Context Engineering layer on top, every piece connects: Step 3 enables Step 4, 5 and 6.',
    contextEngineeringLink: 'Context Engineering →',
    stairwayLink: 'The Stratodata Stairway →',
    /** Step chain (steps 3–6). Labels for 4–6 come from stairwayCopy.stepNames. */
    step3Label: 'Data Engineering',
    chainSubs: ['Single source of truth', 'Dashboards your team trusts', 'Auto-pricing, bidding, forecasting', 'Talk to your data'],
    badge: 'We build this',
  },
  howWeWork: {
    eyebrow: 'How we work',
    title: 'From first source to full platform',
    phases: [
      {
        phase: 'Week 1–2',
        title: 'Discovery & Architecture',
        description: 'We audit every data source — ERP, e-commerce, marketing, WMS, finance. We map the flows, identify gaps, and design the target architecture. You get a blueprint before we write a single line of code.',
      },
      {
        phase: 'Week 2–6',
        title: 'Build Core Pipelines',
        description: 'We connect your critical sources first, establish the core data model, and deliver your first verified dashboards. Early wins build trust. The team sees results before the project is complete.',
      },
      {
        phase: 'Week 6–12',
        title: 'Expand & Automate',
        description: 'Additional sources, deeper models, automated tests and monitoring. We set up alerting so you know when data is late, wrong, or missing — before your team discovers it themselves.',
      },
      {
        phase: 'Ongoing',
        title: 'Run & Evolve',
        description: 'Data infrastructure is never finished. New sources, new business questions, new platforms. We run your pipelines, monitor quality, and evolve the model as your business grows.',
      },
    ],
  },
  cta: {
    title: 'Build your data foundation',
    text: 'Every data-driven capability — automation, AI, reliable reporting — starts here. Let\'s build it right.',
    button: 'Let\'s talk about data engineering',
  },
};

const sk: typeof en = {
  seoTitle: 'Dátové sklady | Stratodata',
  seoDescription:
    'Navrhujeme, staviame a prevádzkujeme dátovú infraštruktúru na Microsoft Azure, MS Fabric, Google Cloud Platform a AWS. Z roztrúsených zdrojov urobíme jeden overený zdroj pravdy.',
  hero: {
    title: 'Dátové sklady',
    subtitle: 'Základ, na ktorom stojí každá firma riadená dátami.',
    text: 'Navrhujeme, staviame a prevádzkujeme dátovú infraštruktúru na Microsoft Azure, MS Fabric, Google Cloud Platform aj AWS. Zo surových zdrojov z nej vznikne jeden overený zdroj pravdy.',
    cta: 'Postavme spoľahlivé dátové základy',
    ctaNote: '',
    stairwayLink: 'Pozrite si celé schodisko →',
  },
  step3: {
    eyebrow: '3. krok Stratodata Stairway',
    title: 'Jeden zdroj pravdy: DWH a BI',
    text: 'Dátové inžinierstvo je zásadný **skok od chaosu k prehľadu** v celej firme. Bez overeného a jednotného dátového modelu stojí automatizácia, AI aj dátová kultúra **na piesku**. Postavíme základy, aby všetko nad nimi fungovalo.',
  },
  platformsSection: {
    title: 'Skúsenosti naprieč platformami',
    subtitle: 'Máme za sebou viacero produkčných nasadení na všetkých hlavných cloudových platformách. Pracujeme tam, kde už vaša firma je.',
    discuss: (name: string) => `Prebrať projekt na ${name} →`,
    servicesLabel: 'Čo dodávame',
  },
  platforms: {
    azure: {
      description: 'Dátové toky od zdroja po report v ekosystéme Microsoftu. Ľahko ich napojíme na vaše Office 365 a Dynamics.',
      services: ['Azure Data Factory', 'Azure Synapse Analytics', 'Azure Data Lake Storage', 'Azure Databricks', 'Azure SQL Database', 'Power BI'],
    },
    fabric: {
      description: 'Nová jednotná analytická platforma od Microsoftu. Fabric sme začali nasadzovať medzi prvými a do produkcie ho dodávame od jeho oficiálneho spustenia (GA).',
      services: ['Lakehouse architektúra', 'Jednotné úložisko OneLake', 'Pipeliny v Data Factory', 'Analytika v reálnom čase', 'Power BI Direct Lake', 'Fabric notebooky'],
    },
    gcp: {
      description: 'Tu máme najviac skúseností. Architektúry postavené na BigQuery zvládnu startup aj veľkú firmu a beží na nich RAS aj pokročilá AI analytika.',
      services: ['BigQuery', 'Cloud Composer (Airflow)', 'Dataform', 'Looker', 'Cloud Storage', 'Vertex AI'],
    },
    aws: {
      description: 'Spoľahlivá dátová infraštruktúra na najväčšom cloude sveta. Dátové toky na mieru pre retailerov, ktorí sa sťahujú z vlastných serverov do cloudu.',
      services: ['Amazon Redshift', 'AWS Glue', 'Data lakes na S3', 'Lambda ETL', 'Amazon RDS', 'SageMaker'],
    },
  },
  capabilitiesSection: {
    title: 'Čo staviame',
    subtitle: 'Dátové inžinierstvo od A po Z. Od surových zdrojov po čísla, podľa ktorých sa dá rozhodovať.',
  },
  capabilities: [
    {
      title: 'Návrh dátového skladu',
      description: 'Dimenzionálne modely, hviezdicové schémy a lakehouse architektúry navrhnuté pre retail. Myslíme na rýchlosť dotazov, na náklady aj na AI, ktorú budete potrebovať zajtra.',
    },
    {
      title: 'ETL / ELT pipeliny',
      description: 'Automatizované dátové toky s monitoringom a upozorneniami. Prenesú dáta z ERP, e-shopu, marketingu aj WMS do jedného overeného modelu. Každý deň čerstvé, bez výnimky.',
    },
    {
      title: 'BI a reportingová vrstva',
      description: 'Dashboardy, ktoré celá firma naozaj používa. Od North Star pohľadu pre vedenie po denné prevádzkové reporty. V Lookeri, v Power BI alebo priamo vo vašich nástrojoch.',
    },
    {
      title: 'Kvalita dát a governance',
      description: 'Dáta nielen presúvame, ale aj overujeme. Automatické testy, kontroly zhody a upozornenia na anomálie zachytia chybu skôr, než sa dostane do dashboardu. Váš tím sa na čísla môže spoľahnúť.',
    },
    {
      title: 'Dátové modely pripravené na AI',
      description: 'Dáta usporiadame tak, aby s nimi AI modely vedeli pracovať. Majú kontext, zmapované vzťahy a jasný význam. Z vášho dátového skladu sa stane základ spoľahlivej AI analytiky.',
    },
    {
      title: 'Migrácie a modernizácia',
      description: 'Presun zo starých serverov do cloudu alebo z jedného cloudu do druhého. Migrácie sme robili na všetkých hlavných platformách a firma pri nich ani raz nestála.',
    },
  ],
  ai: {
    eyebrow: 'Prečo na tom záleží pri AI',
    titleLine1: 'Zlé dáta na vstupe.',
    titleLine2: 'Zlá AI na výstupe.',
    p1: 'AI modely sú len také spoľahlivé ako dáta, z ktorých vychádzajú. Roztrieštené a nejednotné dáta vedú k halucináciám, protirečeniam a rozhodnutiam, ktorým sa nedá veriť.',
    p2: 'Naše dátové inžinierstvo dá AI analytike štruktúrovaný, overený a významovo jasný základ. Vďaka nemu funguje zakaždým, nielen občas.',
    p3: 'Od nášho jednotného retailového dátového modelu po vrstvu Context Engineeringu nad ním všetko nadväzuje. 3. krok otvára cestu k 4., 5. aj 6.',
    contextEngineeringLink: 'Context Engineering →',
    stairwayLink: 'Stratodata Stairway →',
    step3Label: 'Dátové inžinierstvo',
    chainSubs: ['Jeden zdroj pravdy', 'Dashboardy, ktorým tím verí', 'Automatické nacenenie, bidovanie, predikcie', 'Rozprávajte sa so svojimi dátami'],
    badge: 'Toto staviame',
  },
  howWeWork: {
    eyebrow: 'Ako pracujeme',
    title: 'Od prvého zdroja k celej platforme',
    phases: [
      {
        phase: '1. až 2. týždeň',
        title: 'Analýza a architektúra',
        description: 'Prejdeme každý dátový zdroj: ERP, e-shop, marketing, WMS aj financie. Zmapujeme, kade dáta tečú, nájdeme medzery a navrhneme cieľovú architektúru. Plán dostanete skôr, než napíšeme prvý riadok kódu.',
      },
      {
        phase: '2. až 6. týždeň',
        title: 'Prvé dátové toky',
        description: 'Najprv napojíme najdôležitejšie zdroje, postavíme základný dátový model a dodáme prvé overené dashboardy. Rýchle výsledky budujú dôveru. Tím vidí prínos ešte pred koncom projektu.',
      },
      {
        phase: '6. až 12. týždeň',
        title: 'Rozšírenie a automatizácia',
        description: 'Pribudnú ďalšie zdroje, hlbšie modely, automatické testy a monitoring. Nastavíme upozornenia, aby ste o meškajúcich, chybných či chýbajúcich dátach vedeli skôr než váš tím.',
      },
      {
        phase: 'Priebežne',
        title: 'Prevádzka a rozvoj',
        description: 'Dátová infraštruktúra nie je nikdy hotová. Prichádzajú nové zdroje, nové otázky a nové platformy. Staráme sa o vaše dátové toky, strážime kvalitu a model rozvíjame tak, ako rastie vaša firma.',
      },
    ],
  },
  cta: {
    title: 'Postavme spoľahlivé dátové základy',
    text: 'Automatizácia, AI aj spoľahlivý reporting stoja na dátach a všetko to začína tu. Postavme to poriadne.',
    button: 'Porozprávajme sa o dátovom inžinierstve',
  },
};

export const dataWarehousingCopy = { en, sk };
